import { Badge, Tooltip } from 'antd';
import type { ReactNode } from 'react';

import { getItemPrice } from '@eshop/components/catalogue/Cataloguefunctions.tsx';
import type { BestPromotion, FormatedPrice, Item, PromotionLite } from '@eshop/types/catalogue.types.ts';
import { cleanText, formatNumber, getFormattedPrice } from '@tools/utils/common.tsx';

type PromotionWithDescription = PromotionLite & {
  description?: string;
};

export interface CatalogueOfferPresentation {
  hasPromotion: boolean;
  hasDiscountedPrice: boolean;
  badgeText: string | null;
  tooltipTitle: string | null;
  tooltipConditionSummary: string | null;
  tooltipDescription: string | null;
  tooltipDescriptionHtml: string | null;
  formattedOriginalPrice: string | null;
  formattedDiscountedPrice: string | null;
  formattedPrice: FormatedPrice | null;
  promotion: BestPromotion | null;
  promotionInfo: PromotionLite | null;
  isConditionalPrice: boolean;
}

const formatFixedDiscount = (value: number, currency?: string): string => (
  `-${getFormattedPrice(value, currency ?? 'ARS')}`
);


const resolveBadgeText = (
  promotion: PromotionLite | null,
  bestPromotion: BestPromotion | null,
  currency?: string,
  item?: Item,
): string | null => {
  if (!promotion) {
    return cleanText(item?.offer?.beneficioVerbose)
      ?? cleanText(item?.offer?.condicion?.verbose)
      ?? null;
  }

  const catalogBadge = cleanText(promotion.catalog_badge_label);
  if (catalogBadge) return catalogBadge;

  const explicitBadge = cleanText(promotion.badge_label);
  if (explicitBadge) return explicitBadge;

  if (promotion.type === 'percent' && typeof promotion.value === 'number') {
    return `-${formatNumber(promotion.value)}%`;
  }

  if (promotion.type === 'fixed' && typeof promotion.value === 'number') {
    return formatFixedDiscount(promotion.value, currency);
  }

  const verboseBadge =
    cleanText(promotion.beneficioVerbose)
    ?? cleanText(promotion.condicionVerbose)
    ?? cleanText(bestPromotion?.formatedDiscount)
    ?? cleanText(promotion.name);

  return verboseBadge;
};

const resolveTooltipDescription = (
  promotion: PromotionWithDescription | null,
  item?: Item,
): { summary: string | null; text: string | null; html: string | null } => {
  if (!promotion) {
    return {
      summary: null,
      text: cleanText(item?.offer?.description)
        ?? cleanText(item?.offer?.beneficioVerbose)
        ?? cleanText(item?.offer?.condicion?.verbose)
        ?? null,
      html: null,
    };
  }

  const summary = cleanText(promotion.catalog_condition_summary);
  return {
    summary,
    html: summary ? null : cleanText(promotion.description_html),
    text: summary ? null : cleanText(promotion.description)
      ?? cleanText(promotion.beneficioVerbose)
      ?? cleanText(promotion.condicionVerbose)
      ?? cleanText(promotion.limitsVerbose)
      ?? null,
  };
};

export const getCatalogueOfferPresentation = (item: Item): CatalogueOfferPresentation => {
  const formattedPrice = getItemPrice(item);
  const bestPromotion = formattedPrice?.promotion ?? null;
  const promotionInfo =
    bestPromotion?.promotion
    ?? item.promotions?.[0]
    ?? null;
  const hasDiscountedPrice = Boolean(bestPromotion?.formatedDiscountedPrice);
  const badgeText = resolveBadgeText(
    promotionInfo,
    bestPromotion,
    formattedPrice?.currency,
    item,
  );
  const tooltipDescription = resolveTooltipDescription(promotionInfo, item);
  const isConditionalPrice = Boolean(
    bestPromotion?.conditional_price
    || bestPromotion?.promotion?.conditional_price
    || formattedPrice?.conditional_price
    || promotionInfo?.conditional_price
    || promotionInfo?.display_type === 'conditional_catalog_price',
  );
  const tooltipTitle =
    cleanText(promotionInfo?.name)
    ?? cleanText(item.offer?.name)
    ?? badgeText;

  return {
    hasPromotion: Boolean(bestPromotion || promotionInfo || item.offer),
    hasDiscountedPrice,
    badgeText,
    tooltipTitle,
    tooltipConditionSummary: tooltipDescription.summary,
    tooltipDescription: tooltipDescription.text,
    tooltipDescriptionHtml: tooltipDescription.html,
    formattedOriginalPrice: formattedPrice?.formattedPrice ?? null,
    formattedDiscountedPrice: bestPromotion?.formatedDiscountedPrice ?? null,
    formattedPrice,
    promotion: bestPromotion,
    promotionInfo,
    isConditionalPrice,
  };
};

export const renderCatalogueOfferTooltip = (
  offer: Pick<CatalogueOfferPresentation, 'tooltipTitle' | 'tooltipConditionSummary' | 'tooltipDescription' | 'tooltipDescriptionHtml'> & {
    isConditionalPrice?: boolean;
  },
): ReactNode => {
  if (
    !offer.tooltipTitle
    && !offer.tooltipConditionSummary
    && !offer.tooltipDescription
    && !offer.tooltipDescriptionHtml
    && !offer.isConditionalPrice
  ) {
    return null;
  }

  return (
    <div className="catalogue-offer-tooltip">
      {offer.tooltipTitle && (
        <div className="catalogue-offer-tooltip__title">{offer.tooltipTitle}</div>
      )}
      {offer.tooltipConditionSummary ? (
        <div className="catalogue-offer-tooltip__fineprint">{offer.tooltipConditionSummary}</div>
      ) : null}
      {offer.tooltipDescriptionHtml ? (
        <div
          className="catalogue-offer-tooltip__description"
          dangerouslySetInnerHTML={{ __html: offer.tooltipDescriptionHtml }}
        />
      ) : offer.tooltipDescription ? (
        <div className="catalogue-offer-tooltip__description">{offer.tooltipDescription}</div>
      ) : null}
      {offer.isConditionalPrice ? (
        <div className="catalogue-offer-tooltip__fineprint">
          * precio condicionado al cumplimiento de las condiciones de la promoción.
        </div>
      ) : null}
    </div>
  );
};

export interface CatalogueOfferRibbonProps {
  item: Item;
  children: ReactNode;
}

export interface CatalogueOfferPriceProps {
  item: Item;
  containerClassName?: string;
  priceClassName?: string;
  currentClassName?: string;
  originalClassName?: string;
  emptyContent?: ReactNode;
}

export const CatalogueOfferPrice = ({
  item,
  containerClassName = 'catalogue-card__pricing',
  priceClassName = 'catalogue-card__price',
  currentClassName = 'catalogue-card__price--current',
  originalClassName = 'catalogue-card__price--original',
  emptyContent = null,
}: CatalogueOfferPriceProps): ReactNode => {
  const offer = getCatalogueOfferPresentation(item);
  const formatted = offer.formattedPrice;

  if (!formatted) {
    return emptyContent;
  }

  if (!offer.hasDiscountedPrice || !offer.formattedDiscountedPrice) {
    return (
      <div className={containerClassName}>
        <span className={priceClassName}>{formatted.formattedPrice}</span>
      </div>
    );
  }

  return (
    <Tooltip title={renderCatalogueOfferTooltip(offer)} color="magenta" placement="top">
      <div className={`${containerClassName} catalogue-card__pricing--with-offer`}>
        <span className={`${priceClassName} ${originalClassName}`}>
          {formatted.formattedPrice}
        </span>
        {offer.badgeText && (
          <span className="catalogue-card__price--condition">{offer.badgeText}</span>
        )}
        <span className={`${priceClassName} ${currentClassName}`}>
          {offer.formattedDiscountedPrice}{offer.isConditionalPrice ? '*' : ''}
        </span>
      </div>
    </Tooltip>
  );
};

export const CatalogueOfferRibbon = ({ item, children }: CatalogueOfferRibbonProps): ReactNode => {
  const offer = getCatalogueOfferPresentation(item);

  if (!offer.badgeText) {
    return children;
  }

  const tooltip = renderCatalogueOfferTooltip(offer);
  const ribbonText = (
    <Tooltip title={tooltip} color="magenta" placement="topRight">
      <span className="catalogue-offer-ribbon__text" tabIndex={0}>
        {offer.badgeText}
      </span>
    </Tooltip>
  );
  const Ribbon = Badge.Ribbon;

  if (!Ribbon) {
    return (
      <div className="catalogue-offer-ribbon-wrapper">
        <span className="catalogue-offer-ribbon">{ribbonText}</span>
        {children}
      </div>
    );
  }

  return (
    <Ribbon
      rootClassName="catalogue-offer-ribbon-wrapper"
      className="catalogue-offer-ribbon"
      text={ribbonText}
      color="magenta"
      placement="end"
    >
      {children}
    </Ribbon>
  );
};
